import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { usersAPI, authAPI } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { 
  Plus, Search, User, Edit, Trash2, Mail, Phone, Shield, 
  Clock, CheckCircle, XCircle, UserCheck, UserX, Loader2,
  AlertTriangle, History, LogIn, Globe
} from "lucide-react";
import { cn } from "../lib/utils";

export default function Usuarios() {
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loginLogs, setLoginLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("active");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedPendingUser, setSelectedPendingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [processing, setProcessing] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "vendedor",
    phone: "",
    commission_rate: 10
  });
  
  const [approvalForm, setApprovalForm] = useState({
    action: "approve",
    assigned_role: "vendedor",
    commission_rate: 10,
    admin_note: ""
  });

  const roleLabels = {
    admin: "Administrador",
    admin_vendedor: "Admin + Vendedor",
    logistica: "Logística",
    vendedor: "Vendedor",
    mantenimiento: "Mantenimiento",
    vendedor_logistica: "Vendedor + Logística",
    operacion: "Operación",
    pending: "Pendiente"
  };

  const roleColors = {
    admin: "bg-purple-100 text-purple-800",
    admin_vendedor: "bg-indigo-100 text-indigo-800",
    logistica: "bg-amber-100 text-amber-800",
    vendedor: "bg-green-100 text-green-800",
    mantenimiento: "bg-blue-100 text-blue-800",
    vendedor_logistica: "bg-teal-100 text-teal-800",
    operacion: "bg-cyan-100 text-cyan-800",
    pending: "bg-slate-100 text-slate-800"
  };

  const roleLimits = {
    admin: { min: 3, max: 5 },
    admin_vendedor: { min: 0, max: 2 },
    logistica: { min: 1, max: 2 },
    vendedor: { min: 5, max: 10 },
    mantenimiento: { min: 1, max: 2 }
  };
  
  // Roles that can receive commissions
  const rolesWithCommission = ["vendedor", "vendedor_logistica", "admin_vendedor"];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === "logs") {
      fetchLoginLogs();
    }
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, pendingRes] = await Promise.all([
        usersAPI.getAll(),
        usersAPI.getPending()
      ]);
      setUsers(usersRes.data.filter(u => u.status !== "pending" && u.role !== "pending"));
      setPendingUsers(pendingRes.data);
    } catch (error) {
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  };

  const fetchLoginLogs = async () => {
    try {
      const response = await authAPI.getLoginLogs({ limit: 100 });
      setLoginLogs(response.data);
    } catch (error) {
      console.error("Error fetching login logs:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        await usersAPI.update(editingUser.id, updateData);
        toast.success("Usuario actualizado");
      } else {
        await usersAPI.create(formData);
        toast.success("Usuario creado");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    }
  };

  const handleApproval = async (e) => {
    e.preventDefault();
    if (!selectedPendingUser) return;
    
    setProcessing(true);
    try {
      await usersAPI.approveUser(selectedPendingUser.id, approvalForm);
      toast.success(
        approvalForm.action === "approve" 
          ? `Usuario aprobado como ${roleLabels[approvalForm.assigned_role]}`
          : "Solicitud rechazada"
      );
      setApprovalDialogOpen(false);
      setSelectedPendingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al procesar");
    } finally {
      setProcessing(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: "",
      role: user.role,
      phone: user.phone || "",
      commission_rate: user.commission_rate || 10
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Desactivar este usuario?")) return;
    try {
      await usersAPI.delete(id);
      toast.success("Usuario desactivado");
      fetchData();
    } catch (error) {
      toast.error("Error al desactivar");
    }
  };

  const openApprovalDialog = (user) => {
    setSelectedPendingUser(user);
    setApprovalForm({
      action: "approve",
      assigned_role: user.requested_role || "vendedor",
      commission_rate: 10,
      admin_note: ""
    });
    setApprovalDialogOpen(true);
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      name: "",
      email: "",
      password: "",
      role: "vendedor",
      phone: "",
      commission_rate: 10
    });
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const filteredUsers = users.filter(u => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        u.name?.toLowerCase().includes(search) ||
        u.email?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  // Count users by role
  const roleCount = {
    admin: users.filter(u => u.role === "admin").length,
    admin_vendedor: users.filter(u => u.role === "admin_vendedor").length,
    logistica: users.filter(u => u.role === "logistica").length,
    vendedor: users.filter(u => u.role === "vendedor").length,
    mantenimiento: users.filter(u => u.role === "mantenimiento").length
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="usuarios-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900">Usuarios</h1>
            <p className="text-slate-500 mt-1">Gestiona los usuarios del sistema</p>
          </div>
          <Button onClick={openNewDialog} className="gap-2" data-testid="new-user-btn">
            <Plus className="w-4 h-4" />
            Crear Usuario
          </Button>
        </div>

        {/* Role Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(roleLimits).map(([role, limits]) => (
            <Card key={role} className={cn(
              "border-l-4",
              roleCount[role] >= limits.min ? "border-l-green-500" : "border-l-amber-500"
            )}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{roleLabels[role]}</p>
                    <p className="text-2xl font-bold">
                      {roleCount[role]} / {limits.min}-{limits.max}
                    </p>
                  </div>
                  {roleCount[role] < limits.min && (
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pending Users Alert */}
        {pendingUsers.length > 0 && (
          <Card className="border-amber-300 bg-amber-50">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-amber-800">
                  {pendingUsers.length} usuario(s) pendiente(s) de aprobación
                </p>
                <p className="text-sm text-amber-600">
                  Revisa las solicitudes de registro en la pestaña "Pendientes"
                </p>
              </div>
              <Button 
                variant="outline" 
                className="border-amber-400 text-amber-700"
                onClick={() => setActiveTab("pending")}
              >
                Revisar
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por nombre o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="active" data-testid="tab-active">
              <UserCheck className="w-4 h-4 mr-2" />
              Activos ({users.filter(u => u.is_active).length})
            </TabsTrigger>
            <TabsTrigger value="pending" data-testid="tab-pending">
              <Clock className="w-4 h-4 mr-2" />
              Pendientes ({pendingUsers.length})
            </TabsTrigger>
            <TabsTrigger value="inactive" data-testid="tab-inactive">
              <UserX className="w-4 h-4 mr-2" />
              Inactivos
            </TabsTrigger>
            <TabsTrigger value="logs" data-testid="tab-logs">
              <History className="w-4 h-4 mr-2" />
              Bitácora
            </TabsTrigger>
          </TabsList>

          {/* Active Users */}
          <TabsContent value="active">
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
                  </div>
                ) : filteredUsers.filter(u => u.is_active).length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <User className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No hay usuarios activos</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Usuario</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Teléfono</TableHead>
                        <TableHead>Rol</TableHead>
                        <TableHead>Comisión</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.filter(u => u.is_active).map((user) => (
                        <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-slate-500" />
                              </div>
                              <div>
                                <p className="font-medium">{user.name}</p>
                                {user.is_super_admin && (
                                  <Badge className="bg-purple-100 text-purple-800 text-xs">
                                    Super Admin
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 text-slate-600">
                              <Mail className="w-4 h-4" />
                              {user.email}
                            </div>
                          </TableCell>
                          <TableCell>
                            {user.phone && (
                              <div className="flex items-center gap-2 text-slate-600">
                                <Phone className="w-4 h-4" />
                                {user.phone}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={cn(roleColors[user.role])}>
                              <Shield className="w-3 h-3 mr-1" />
                              {roleLabels[user.role] || user.role}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {rolesWithCommission.includes(user.role) && user.commission_rate && (
                              <span className="font-medium">{user.commission_rate}%</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Activo
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button size="sm" variant="ghost" onClick={() => handleEdit(user)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              {!user.is_super_admin && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="text-red-600"
                                  onClick={() => handleDelete(user.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pending Users */}
          <TabsContent value="pending">
            <Card>
              <CardContent className="p-0">
                {pendingUsers.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
                    <p>No hay solicitudes pendientes</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Solicitante</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Teléfono</TableHead>
                        <TableHead>Rol Solicitado</TableHead>
                        <TableHead>Fecha</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingUsers.map((user) => (
                        <TableRow key={user.id} data-testid={`pending-row-${user.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                                <Clock className="w-4 h-4 text-amber-500" />
                              </div>
                              <p className="font-medium">{user.name}</p>
                            </div>
                          </TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>{user.phone || "-"}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {roleLabels[user.requested_role] || user.requested_role}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {user.created_at?.slice(0, 10)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => openApprovalDialog(user)}
                                data-testid={`approve-btn-${user.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Revisar
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Inactive Users */}
          <TabsContent value="inactive">
            <Card>
              <CardContent className="p-0">
                {filteredUsers.filter(u => !u.is_active).length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <User className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No hay usuarios inactivos</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Usuario</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Rol</TableHead>
                        <TableHead>Estado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.filter(u => !u.is_active).map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>{user.name}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{roleLabels[user.role]}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-red-100 text-red-800">
                              <XCircle className="w-3 h-3 mr-1" />
                              Inactivo
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Login Logs */}
          <TabsContent value="logs">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Bitácora de Accesos
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loginLogs.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <History className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No hay registros de acceso</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fecha/Hora</TableHead>
                        <TableHead>Usuario</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead>IP</TableHead>
                        <TableHead>Motivo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loginLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell className="text-sm">
                            {new Date(log.timestamp).toLocaleString("es-MX", {
                              day: "2-digit",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit"
                            })}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {log.success ? (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              ) : (
                                <XCircle className="w-4 h-4 text-red-500" />
                              )}
                              <span>{log.user_name || "-"}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {log.user_email || log.email}
                          </TableCell>
                          <TableCell>
                            <Badge className={log.success ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                              {log.success ? (
                                <><LogIn className="w-3 h-3 mr-1" /> Exitoso</>
                              ) : (
                                <><XCircle className="w-3 h-3 mr-1" /> Fallido</>
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500 font-mono">
                            <div className="flex items-center gap-1">
                              <Globe className="w-3 h-3" />
                              {log.ip_address}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {log.failure_reason ? (
                              <Badge variant="outline" className="text-xs">
                                {log.failure_reason === "invalid_credentials" && "Credenciales inválidas"}
                                {log.failure_reason === "pending_approval" && "Cuenta pendiente"}
                                {log.failure_reason === "rejected" && "Cuenta rechazada"}
                                {log.failure_reason === "inactive" && "Usuario inactivo"}
                              </Badge>
                            ) : "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingUser ? "Editar Usuario" : "Crear Usuario"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>{editingUser ? "Nueva Contraseña (dejar vacío para mantener)" : "Contraseña *"}</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  required={!editingUser}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Rol *</Label>
                  <Select value={formData.role} onValueChange={(v) => setFormData(prev => ({ ...prev, role: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Administrador ({roleCount.admin}/{roleLimits.admin.max})</SelectItem>
                      <SelectItem value="admin_vendedor">Admin + Vendedor ({roleCount.admin_vendedor}/{roleLimits.admin_vendedor.max})</SelectItem>
                      <SelectItem value="logistica">Logística ({roleCount.logistica}/{roleLimits.logistica.max})</SelectItem>
                      <SelectItem value="vendedor">Vendedor ({roleCount.vendedor}/{roleLimits.vendedor.max})</SelectItem>
                      <SelectItem value="vendedor_logistica">Vendedor + Logística (combinado)</SelectItem>
                      <SelectItem value="mantenimiento">Mantenimiento ({roleCount.mantenimiento}/{roleLimits.mantenimiento.max})</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {rolesWithCommission.includes(formData.role) && (
                  <div className="space-y-2">
                    <Label>Comisión (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.commission_rate}
                      onChange={(e) => setFormData(prev => ({ ...prev, commission_rate: parseFloat(e.target.value) }))}
                    />
                  </div>
                )}
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit">
                  {editingUser ? "Guardar" : "Crear"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Approval Dialog */}
        <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Revisar Solicitud de Registro</DialogTitle>
            </DialogHeader>
            
            {selectedPendingUser && (
              <form onSubmit={handleApproval} className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium">{selectedPendingUser.name}</p>
                      <p className="text-sm text-slate-500">{selectedPendingUser.email}</p>
                    </div>
                  </div>
                  {selectedPendingUser.phone && (
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {selectedPendingUser.phone}
                    </p>
                  )}
                  <p className="text-sm">
                    <span className="text-slate-500">Rol solicitado:</span>{" "}
                    <Badge variant="outline">
                      {roleLabels[selectedPendingUser.requested_role] || selectedPendingUser.requested_role}
                    </Badge>
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Acción</Label>
                  <Select 
                    value={approvalForm.action} 
                    onValueChange={(v) => setApprovalForm(prev => ({ ...prev, action: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="approve">
                        <span className="flex items-center gap-2 text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          Aprobar
                        </span>
                      </SelectItem>
                      <SelectItem value="reject">
                        <span className="flex items-center gap-2 text-red-600">
                          <XCircle className="w-4 h-4" />
                          Rechazar
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {approvalForm.action === "approve" && (
                  <>
                    <div className="space-y-2">
                      <Label>Rol a Asignar *</Label>
                      <Select 
                        value={approvalForm.assigned_role} 
                        onValueChange={(v) => setApprovalForm(prev => ({ ...prev, assigned_role: v }))}
                      >
                        <SelectTrigger data-testid="assign-role-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">
                            Administrador ({roleCount.admin}/{roleLimits.admin.max})
                          </SelectItem>
                          <SelectItem value="admin_vendedor">
                            Admin + Vendedor ({roleCount.admin_vendedor}/{roleLimits.admin_vendedor.max})
                          </SelectItem>
                          <SelectItem value="logistica">
                            Logística ({roleCount.logistica}/{roleLimits.logistica.max})
                          </SelectItem>
                          <SelectItem value="vendedor">
                            Vendedor ({roleCount.vendedor}/{roleLimits.vendedor.max})
                          </SelectItem>
                          <SelectItem value="vendedor_logistica">
                            Vendedor + Logística (acceso combinado)
                          </SelectItem>
                          <SelectItem value="mantenimiento">
                            Mantenimiento ({roleCount.mantenimiento}/{roleLimits.mantenimiento.max})
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {rolesWithCommission.includes(approvalForm.assigned_role) && (
                      <div className="space-y-2">
                        <Label>Comisión (%)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={approvalForm.commission_rate}
                          onChange={(e) => setApprovalForm(prev => ({ 
                            ...prev, 
                            commission_rate: parseFloat(e.target.value) 
                          }))}
                        />
                      </div>
                    )}
                  </>
                )}
                
                <div className="space-y-2">
                  <Label>Nota {approvalForm.action === "reject" ? "*" : "(opcional)"}</Label>
                  <Textarea
                    value={approvalForm.admin_note}
                    onChange={(e) => setApprovalForm(prev => ({ ...prev, admin_note: e.target.value }))}
                    placeholder={
                      approvalForm.action === "approve" 
                        ? "Agregar nota opcional..." 
                        : "Motivo del rechazo..."
                    }
                    required={approvalForm.action === "reject"}
                  />
                </div>
                
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setApprovalDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={processing}
                    className={approvalForm.action === "reject" ? "bg-red-600 hover:bg-red-700" : ""}
                    data-testid="submit-approval-btn"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Procesando...
                      </>
                    ) : approvalForm.action === "approve" ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Aprobar Usuario
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 mr-2" />
                        Rechazar
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
